// manageThemisRuleSets.ts

import axios, { AxiosResponse } from "axios";
import { ThemisPhase } from "./ThemisRuleList";

type IdResponseDTO = { id: number };

// ----------------- DTO-aligned types -----------------

export interface ThemisRuleSetListItemDto {
  id: number;
  key: string;
  name: string;
  isEnabled: boolean;
  priority: number;
}

export interface ThemisRuleSetRuleLinkDto {
  id: number;
  themisRuleId: number;
  ruleName: string;
  ruleTargetScopeKey: string;
  rulePhase: ThemisPhase | string;
  sortOrder: number;
  isEnabled: boolean;
  overridePriority?: number | null;
}

export interface ThemisRuleSetDto {
  id: number;
  key: string;
  name: string;
  description?: string | null;
  isEnabled: boolean;
  priority: number;
  ruleLinks: ThemisRuleSetRuleLinkDto[];
}

export type CreateThemisRuleSetRequestDto = {
  key: string;
  name: string;
  description?: string | null;
  isEnabled?: boolean; // default true on backend
  priority?: number; // default 0 on backend
};

export type UpdateThemisRuleSetRequestDto = {
  // backend does NOT allow key update
  name: string;
  description?: string | null;
  isEnabled: boolean;
  priority: number;
};

export type AttachRuleToRuleSetRequestDto = {
  themisRuleId: number;
  sortOrder: number;
  isEnabled?: boolean; // default true on backend
  overridePriority?: number | null;
};

export type UpdateRuleSetRuleRequestDto = {
  sortOrder: number;
  isEnabled: boolean;
  overridePriority?: number | null;
};

// ----------------- base url -----------------

const baseURL = process.env.REACT_APP_API_URL;

if (!baseURL) {
  console.error("[manageThemisRuleSets] REACT_APP_API_URL is not set.");
}

const themisAdminBase = `${baseURL}/admin/themis`;

// ----------------- endpoints -----------------

export function getRuleSets(
  pagination: { pageNumber: number; pageSize: number },
  q?: string
): Promise<AxiosResponse<ThemisRuleSetListItemDto[]>> {
  return axios.get(`${themisAdminBase}/rulesets`, {
    params: { ...pagination, q },
  });
}

export function getRuleSet(id: number): Promise<AxiosResponse<ThemisRuleSetDto>> {
  return axios.get(`${themisAdminBase}/rulesets/${id}`);
}

export function createRuleSet(
  req: CreateThemisRuleSetRequestDto
): Promise<AxiosResponse<IdResponseDTO>> {
  return axios.post(`${themisAdminBase}/rulesets`, req);
}

export function updateRuleSet(
  id: number,
  req: UpdateThemisRuleSetRequestDto
): Promise<AxiosResponse<void>> {
  return axios.put(`${themisAdminBase}/rulesets/${id}`, req);
}

export function deleteRuleSet(id: number): Promise<AxiosResponse<void>> {
  return axios.delete(`${themisAdminBase}/rulesets/${id}`);
}

export function attachRuleToRuleSet(
  ruleSetId: number,
  req: AttachRuleToRuleSetRequestDto
): Promise<AxiosResponse<IdResponseDTO>> {
  return axios.post(`${themisAdminBase}/rulesets/${ruleSetId}/rules`, req);
}

export function updateRuleSetRule(
  ruleSetRuleLinkId: number,
  req: UpdateRuleSetRuleRequestDto
): Promise<AxiosResponse<void>> {
  return axios.put(`${themisAdminBase}/ruleset-rules/${ruleSetRuleLinkId}`, req);
}

export function deleteRuleSetRule(ruleSetRuleLinkId: number): Promise<AxiosResponse<void>> {
  return axios.delete(`${themisAdminBase}/ruleset-rules/${ruleSetRuleLinkId}`);
}

// ----------------- helpers -----------------

export function slugifyKeyFromName(name: string): string {
  return name
    .trim()
    .replace(/[^\w\s-]/g, "")
    .replace(/\s+/g, "")
    .slice(0, 100);
}
